import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  validatePassword,
  isFormValid,
  getFirstErrorField
} from '../../services';
import { httpService } from '../../services/httpService';
import { API_BASE_URL } from '../../services/api';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [onValueChange, setOnValueChange] = useState(false);
  const [errors, setErrors] = useState({
    password: '',
    confirmPassword: ''
  });
  const [touched, setTouched] = useState({
    password: false,
    confirmPassword: false
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if user details exist and have email
    if (!userDetails || !userDetails.email_id) {
      toast.error('User session not found. Please login again.', { position: 'top-right', autoClose: 3000 });
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  }, [userDetails, navigate]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOnValueChange(true);
    const value = e.target.value;
    setPassword(value);

    if (touched.password) {
      const error = validatePassword(value);
      setErrors({
        ...errors,
        password: error
      });
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOnValueChange(true);
    const value = e.target.value;
    setConfirmPassword(value);

    if (touched.confirmPassword) {
      const error = password !== value ? 'Passwords do not match' : '';
      setErrors({
        ...errors,
        confirmPassword: error
      });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!onValueChange) return;
    const { name, value } = e.target;
    setTouched({
      ...touched,
      [name]: true
    });

    let error = '';
    if (name === 'password') {
      error = validatePassword(value);
    } else if (name === 'confirmPassword') {
      error = password !== value ? 'Passwords do not match' : '';
    }

    setErrors({
      ...errors,
      [name]: error
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if user email exists in Redux
    if (!userDetails || !userDetails.email_id) {
      toast.error('User email not found. Please login again.', { position: 'top-right', autoClose: 3000 });
      navigate('/login');
      return;
    }

    setTouched({
      password: true,
      confirmPassword: true
    });

    const passwordError = validatePassword(password);
    const confirmPasswordError = password !== confirmPassword ? 'Passwords do not match' : '';

    const newErrors = {
      password: passwordError || '',
      confirmPassword: confirmPasswordError || ''
    };

    setErrors(newErrors);

    if (!isFormValid(newErrors)) {
      const firstErrorField = getFirstErrorField(newErrors);
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        element?.focus();
      }
      return;
    }

    setIsLoading(true);
    try {
      const resetPasswordPayload = {
        request_id: "reset_password_req",
        timestamp: new Date().toISOString(),
        action: "reset_password",
        data: {
          email_id: userDetails.email_id,
          new_password: password,
          confirm_password: confirmPassword
        }
      };

      type ResetPasswordResponse = { status: number; [key: string]: any };
      const resetResponse = await httpService.post<ResetPasswordResponse>(`${API_BASE_URL}/user/resetPassword`, resetPasswordPayload);
      
      console.log("Reset Password Response:", resetResponse);
      
      if (resetResponse.data.status === 200) {
        setErrors({
          password: '',
          confirmPassword: ''
        });
        toast.success('Password reset successful!', { position: 'top-right', autoClose: 1500 });
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      } else {
        toast.error(resetResponse.data.message || 'Password reset failed. Please try again.', { position: 'top-right', autoClose: 3000 });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Password reset failed. Please try again.';
      toast.error(errorMessage, { position: 'top-right', autoClose: 3000 });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          position: 'absolute',
          top: 14,
          left: 24,
          zIndex: 1000,
        }}
      >
        <img
          src="/images/sampark-logo.png"
          alt="Company Logo"
          style={{ height: '80px', width: 'auto' }}
        />
      </Box>
      <Box
        sx={{
          marginTop: 20,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography component="h1" variant="h4" gutterBottom>
              RESET PASSWORD
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              Enter your new password for: <strong>{userDetails?.email_id}</strong>
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="off"
                autoFocus
                value={password}
                onChange={handlePasswordChange}
                onBlur={handleBlur}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="off"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                onBlur={handleBlur}
                error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                helperText={touched.confirmPassword && errors.confirmPassword}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={handleClickShowConfirmPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={
                  !password ||
                  !confirmPassword ||
                  Object.values(errors).some(error => error !== '') ||
                  isLoading
                }
                sx={{ mt: 3, mb: 2, py: 1.5 }}
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              Remember your password?
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/login')}
                sx={{ textDecoration: 'none', marginLeft: '5px' }}
              >
                Back to Login
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword;
